import React from "react";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Activity, Clock, Users, X } from "lucide-react";
import { format } from "date-fns";

export function TaskProgressLogDialog({
  isProgressLogOpen, setIsProgressLogOpen,
  progressLogCategoryFilter, setProgressLogCategoryFilter,
  progressLogUserFilter, setProgressLogUserFilter,
  progressLogStartDate, setProgressLogStartDate,
  progressLogEndDate, setProgressLogEndDate,
  progressLogLoading, progressLogEntries, progressLogAssignees,
  categories,
}: any) {
  return (
      <Dialog open={isProgressLogOpen} onOpenChange={setIsProgressLogOpen}>
        <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto" data-testid="dialog-progress-log">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Progress Activity Log
            </DialogTitle>
            <DialogDescription>
              View all task progress entries across your tasks
            </DialogDescription>
          </DialogHeader>
          
          {/* Filter Controls */}
          <div className="flex flex-wrap gap-4 py-4 border-b">
            <div className="flex-1 min-w-[150px]">
              <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Category</label>
              <Select 
                value={progressLogCategoryFilter} 
                onValueChange={setProgressLogCategoryFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <CategorySelectGroups categories={categories} />
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Assigned To</label>
              <Select 
                value={progressLogUserFilter} 
                onValueChange={setProgressLogUserFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {progressLogAssignees.map((assignee: any) => (
                    <SelectItem key={assignee.id} value={assignee.id}>{assignee.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Start Date</label>
                <Input
                  type="date"
                  value={progressLogStartDate}
                  onChange={(e) => setProgressLogStartDate(e.target.value)}
                  className="w-[130px]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">End Date</label>
                <Input
                  type="date"
                  value={progressLogEndDate}
                  onChange={(e) => setProgressLogEndDate(e.target.value)}
                  className="w-[130px]"
                />
              </div>
            </div>
            {(progressLogCategoryFilter !== "all" || progressLogUserFilter !== "all" || progressLogStartDate || progressLogEndDate) && (
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setProgressLogCategoryFilter("all");
                    setProgressLogUserFilter("all");
                    setProgressLogStartDate("");
                    setProgressLogEndDate("");
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
              </div>
            )}
          </div>

          {/* Progress Entries List */}
          <ScrollArea className="h-[400px]" data-testid="progress-log-entries">
            {progressLogLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">Loading progress entries...</div>
              </div>
            ) : progressLogEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Activity className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No progress entries found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {progressLogCategoryFilter !== "all" || progressLogUserFilter !== "all" || progressLogStartDate || progressLogEndDate
                    ? "Try adjusting your filters"
                    : "Progress entries will appear here as you track task progress"}
                </p>
              </div>
            ) : (
              <div className="space-y-3 pr-4">
                {progressLogEntries.map((entry: any) => (
                  <Card key={entry.id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-foreground truncate">
                            {entry.taskTitle || "Unknown Task"}
                          </h4>
                          {entry.taskCategory && (
                            <Badge variant="secondary" className="text-xs">{entry.taskCategory}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{entry.notes}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {entry.user.firstName} {entry.user.lastName}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(new Date(entry.createdAt), "MMM d, yyyy 'at' h:mm a")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsProgressLogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
